import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [mobileGuideOpen, setMobileGuideOpen] = useState(false);
  const guideRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (guideRef.current && !guideRef.current.contains(e.target as Node)) {
        setGuideOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-sm border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <nav className="w-full px-4 md:px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between h-16">
          <Link
            to="/"
            className="font-heading text-xl md:text-2xl font-bold text-foreground-900 hover:text-primary-600 transition-colors whitespace-nowrap"
          >
            Sugargoo Spreadsheet
          </Link>

          <div className="hidden md:flex items-center gap-5">
            <Link
              to="/"
              className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap flex items-center gap-1"
            >
              <i className="ri-home-4-line text-sm"></i>
              Home
            </Link>

            {/* Shopping Guide Dropdown */}
            <div className="relative" ref={guideRef}>
              <button
                onClick={() => setGuideOpen(!guideOpen)}
                className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap flex items-center gap-1"
              >
                <i className="ri-book-open-line text-sm"></i>
                Shopping Guide
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${guideOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {guideOpen && (
                <div className="absolute top-full mt-2 left-0 bg-background-50 border border-background-200/70 rounded-lg shadow-lg py-2 min-w-[300px] z-50 max-h-[60vh] overflow-y-auto">
                  <div className="px-4 py-1.5">
                    <span className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider">Expert Guides</span>
                  </div>
                  {articles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/article/${article.slug}`}
                      onClick={() => setGuideOpen(false)}
                      className="block px-4 py-2 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors whitespace-nowrap"
                    >
                      {article.title.length > 48 ? article.title.slice(0, 48) + '...' : article.title}
                    </Link>
                  ))}
                  <div className="border-t border-background-200/50 mt-1 pt-1">
                    <Link
                      to="/sugargoo-spreadsheet-guide"
                      onClick={() => setGuideOpen(false)}
                      className="block px-4 py-2 text-sm font-semibold text-primary-600 hover:bg-background-100 transition-colors whitespace-nowrap"
                    >
                      Complete Guide →
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <a href="#categories" className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap">
              Categories
            </a>
            <a href="#articles" className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap">
              Articles
            </a>
            <a href="#shipping" className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap">
              Shipping
            </a>
            <a href="#discount" className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap">
              Discounts
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="bg-primary-500 text-background-50 px-5 py-2 rounded-md text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-800"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Enhanced Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200/70 max-h-[75vh] overflow-y-auto">
            <div className="py-3 px-4 space-y-1">
              {/* Home */}
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2.5 text-sm font-semibold text-foreground-900 py-2.5 px-2 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-home-4-line text-foreground-600"></i>
                Home
              </Link>

              {/* Shopping Guide - accordion */}
              <div>
                <button
                  onClick={() => setMobileGuideOpen(!mobileGuideOpen)}
                  className="flex items-center justify-between w-full text-sm font-semibold text-foreground-900 py-2.5 px-2 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap"
                >
                  <span className="flex items-center gap-2.5">
                    <i className="ri-book-open-line text-foreground-600"></i>
                    Shopping Guide
                  </span>
                  <i className={`ri-arrow-down-s-line text-foreground-500 transition-transform ${mobileGuideOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {mobileGuideOpen && (
                  <div className="ml-8 mt-1 space-y-0.5 pb-1">
                    {articles.map((article) => (
                      <Link
                        key={article.slug}
                        to={`/article/${article.slug}`}
                        onClick={() => { setMobileOpen(false); setMobileGuideOpen(false); }}
                        className="block text-sm text-foreground-600 py-2 px-2 rounded-md hover:bg-background-100 hover:text-primary-600 transition-colors whitespace-nowrap"
                      >
                        {article.title.length > 40 ? article.title.slice(0, 40) + '...' : article.title}
                      </Link>
                    ))}
                    <Link
                      to="/sugargoo-spreadsheet-guide"
                      onClick={() => { setMobileOpen(false); setMobileGuideOpen(false); }}
                      className="block text-sm font-semibold text-primary-600 py-2 px-2 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap"
                    >
                      Complete Guide →
                    </Link>
                  </div>
                )}
              </div>

              {/* Categories */}
              <div className="pt-2">
                <span className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider px-2">Categories</span>
                <div className="mt-1 grid grid-cols-2 gap-0.5">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2 text-xs text-foreground-600 py-2 px-2 rounded-md hover:bg-background-100 hover:text-primary-600 transition-colors whitespace-nowrap"
                    >
                      <i className={`${cat.icon} text-foreground-400 text-xs`}></i>
                      {cat.name}
                    </a>
                  ))}
                </div>
              </div>

              {/* Quick Links */}
              <div className="pt-2">
                <span className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider px-2">Quick Links</span>
                <div className="mt-1 space-y-0.5">
                  <a href="#why-choose" className="flex items-center gap-2.5 text-sm text-foreground-600 py-2 px-2 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap" onClick={() => setMobileOpen(false)}>
                    <i className="ri-star-line text-foreground-400"></i>
                    Why Choose
                  </a>
                  <a href="#review" className="flex items-center gap-2.5 text-sm text-foreground-600 py-2 px-2 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap" onClick={() => setMobileOpen(false)}>
                    <i className="ri-chat-check-line text-foreground-400"></i>
                    Review
                  </a>
                  <a href="#shipping" className="flex items-center gap-2.5 text-sm text-foreground-600 py-2 px-2 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap" onClick={() => setMobileOpen(false)}>
                    <i className="ri-ship-line text-foreground-400"></i>
                    Shipping Guide
                  </a>
                  <a href="#discount" className="flex items-center gap-2.5 text-sm text-foreground-600 py-2 px-2 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap" onClick={() => setMobileOpen(false)}>
                    <i className="ri-coupon-line text-foreground-400"></i>
                    Discounts
                  </a>
                  <a href="#articles" className="flex items-center gap-2.5 text-sm text-foreground-600 py-2 px-2 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap" onClick={() => setMobileOpen(false)}>
                    <i className="ri-article-line text-foreground-400"></i>
                    All Articles
                  </a>
                </div>
              </div>

              {/* CTA */}
              <div className="pt-4 pb-1">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="block w-full bg-primary-500 text-background-50 text-center py-3 rounded-md text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap"
                  onClick={() => setMobileOpen(false)}
                >
                  Buy It Now
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}